import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Loader2,
  Users as UsersIcon,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Shield,
  UserCheck,
  Clock,
  Crown,
  DollarSign,
  UserPlus,
  Upload,
  Download,
  FileText,
  Key,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const REP_TIERS = ['Broker 1', 'Broker 2', 'Manager', 'Director', 'Retail Rep'];
const USER_ROLES = ['Admin', 'SalesRep', 'BackOffice'];

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [hierarchyDialogOpen, setHierarchyDialogOpen] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newTier, setNewTier] = useState('');
  const [newManagerId, setNewManagerId] = useState('');
  const [newDirectorId, setNewDirectorId] = useState('');
  const [redlinePpw, setRedlinePpw] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  
  // Password reset dialog
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Delete user dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'SalesRep',
    rep_tier: '',
  });

  const loadUsers = useCallback(async () => {
    try {
      const [usersRes, pendingRes] = await Promise.all([
        usersAPI.getAll(),
        usersAPI.getPending(),
      ]);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleApprove = async (userId) => {
    setUpdatingId(userId);
    try {
      await usersAPI.approve(userId);
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      const approvedUser = pendingUsers.find(u => u.id === userId);
      if (approvedUser) {
        setUsers(prev => [...prev, { ...approvedUser, is_approved: true }]);
      }
      toast.success('User approved successfully');
    } catch (error) {
      toast.error('Failed to approve user');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleReject = async (userId) => {
    setUpdatingId(userId);
    try {
      await usersAPI.reject(userId);
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User rejected');
    } catch (error) {
      toast.error('Failed to reject user');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRoleChange = async () => {
    if (!selectedUser || !newRole) return;
    
    setUpdatingId(selectedUser.id);
    try {
      await usersAPI.updateRole(selectedUser.id, newRole);
      setUsers(prev => prev.map(u => 
        u.id === selectedUser.id ? { ...u, role: newRole } : u
      ));
      toast.success('Role updated successfully');
      setRoleDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleTierChange = async () => {
    if (!selectedUser) return;
    
    setUpdatingId(selectedUser.id);
    try {
      const updateData = { rep_tier: newTier === 'none' ? null : newTier };
      // Only include redline_ppw if the tier is Director and value is provided
      if (newTier === 'Director' && redlinePpw !== '') {
        updateData.redline_ppw = parseFloat(redlinePpw) || null;
      } else if (newTier !== 'Director') {
        updateData.redline_ppw = null; // Clear redline if not Director
      }
      
      await api.put(`/users/${selectedUser.id}`, updateData);
      setUsers(prev => prev.map(u => 
        u.id === selectedUser.id ? { 
          ...u, 
          rep_tier: newTier === 'none' ? null : newTier,
          redline_ppw: updateData.redline_ppw 
        } : u
      ));
      toast.success('Commission tier updated successfully');
      setTierDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update commission tier');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleHierarchyChange = async () => {
    if (!selectedUser) return;
    
    setUpdatingId(selectedUser.id);
    try {
      await api.put(`/users/${selectedUser.id}`, { 
        manager_id: newManagerId === 'none' ? null : newManagerId || null,
        director_id: newDirectorId === 'none' ? null : newDirectorId || null 
      });
      
      // Find manager/director names
      const manager = users.find(u => u.id === newManagerId);
      const director = users.find(u => u.id === newDirectorId);
      
      setUsers(prev => prev.map(u => 
        u.id === selectedUser.id ? { 
          ...u, 
          manager_id: newManagerId || null,
          director_id: newDirectorId || null,
          manager_name: manager?.name || null,
          director_name: director?.name || null
        } : u
      ));
      toast.success('Team hierarchy updated successfully');
      setHierarchyDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update team hierarchy');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddUser = async () => {
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (newUserData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setAddingUser(true);
    try {
      const response = await usersAPI.create({
        name: newUserData.name,
        email: newUserData.email,
        password: newUserData.password,
        role: newUserData.role,
        rep_tier: newUserData.rep_tier || null,
      });
      
      setUsers(prev => [...prev, response.data]);
      toast.success(`${newUserData.name} has been added to your team`);
      setAddUserDialogOpen(false);
      setNewUserData({
        name: '',
        email: '',
        password: '',
        role: 'SalesRep',
        rep_tier: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await usersAPI.getImportTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user_import_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImportUsers = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csvContent = e.target.result;
          const response = await api.post('/users/import', null, { 
            params: { file_content: csvContent }
          });
          
          const { created, errors } = response.data;
          
          if (created.length > 0) {
            // Refresh users list
            const usersResponse = await usersAPI.getAll();
            setUsers(usersResponse.data);
            toast.success(`Successfully imported ${created.length} users`);
          }
          
          if (errors.length > 0) {
            toast.error(`${errors.length} rows had errors: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '...' : ''}`);
          }
          
          setImportDialogOpen(false);
          setImportFile(null);
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Import failed');
        } finally {
          setImporting(false);
        }
      };
      reader.readAsText(importFile);
    } catch (error) {
      toast.error('Failed to read file');
      setImporting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      await api.post(`/users/${selectedUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset successfully for ${selectedUser.name}`);
      setResetPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleDeleteUser = async () => {
    setDeletingUser(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
      toast.success(`User ${selectedUser.name} has been deleted`);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleDialogOpen(true);
  };

  const openTierDialog = (user) => {
    setSelectedUser(user);
    setNewTier(user.rep_tier || 'none');
    setRedlinePpw(user.redline_ppw != null ? String(user.redline_ppw) : '');
    setTierDialogOpen(true);
  };

  const openHierarchyDialog = (user) => {
    setSelectedUser(user);
    setNewManagerId(user.manager_id || 'none');
    setNewDirectorId(user.director_id || 'none');
    setHierarchyDialogOpen(true);
  };

  const getRoleBadge = (role) => {
    const styles = {
      'Admin': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      'SalesRep': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'BackOffice': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    };
    return styles[role] || 'bg-slate-100 text-slate-700';
  };

  const getTierBadge = (tier) => {
    if (!tier) return null;
    const styles = {
      'Director': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      'Manager': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'Broker 2': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'Broker 1': 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
      'Retail Rep': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    };
    return styles[tier] || 'bg-slate-100 text-slate-600';
  };

  const approvedUsers = users.filter(u => u.is_approved);
  const managers = approvedUsers.filter(u => u.rep_tier === 'Manager');
  const directors = approvedUsers.filter(u => u.rep_tier === 'Director');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            User Management
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage team members, commission tiers, and team hierarchy
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setImportDialogOpen(true)}
            className="border-slate-300 dark:border-slate-600"
            data-testid="import-users-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button
            onClick={() => setAddUserDialogOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20"
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Team Member
          </Button>
        </div>
      </div>

      {/* Pending Approvals */}
      {pendingUsers.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20" data-testid="pending-approvals-section">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <Clock className="w-5 h-5" />
              Pending Approvals ({pendingUsers.length})
            </CardTitle>
            <CardDescription className="text-amber-700 dark:text-amber-300">
              Review and approve new user registrations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingUsers.map((pendingUser) => (
                <div
                  key={pendingUser.id}
                  className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-lg border border-amber-200 dark:border-amber-800"
                  data-testid={`pending-user-${pendingUser.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                      <span className="font-medium text-amber-700">
                        {pendingUser.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {pendingUser.name}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {pendingUser.email}
                      </p>
                    </div>
                    <Badge className={getRoleBadge(pendingUser.role)}>
                      {pendingUser.role}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 mr-4">
                      {new Date(pendingUser.created_at).toLocaleDateString()}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReject(pendingUser.id)}
                      disabled={updatingId === pendingUser.id}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`reject-${pendingUser.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApprove(pendingUser.id)}
                      disabled={updatingId === pendingUser.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`approve-${pendingUser.id}`}
                    >
                      {updatingId === pendingUser.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Users */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-emerald-600" />
            Active Users ({approvedUsers.length})
          </CardTitle>
          <CardDescription>
            Manage roles, commission tiers, and team hierarchy for approved team members
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {approvedUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Commission Tier</TableHead>
                    <TableHead>Reports To</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvedUsers.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                            <span className="font-medium text-emerald-700">
                              {user.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {user.name}
                              {user.id === currentUser?.id && (
                                <span className="text-xs text-slate-400 ml-2">(you)</span>
                              )}
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getRoleBadge(user.role)}>
                          <Shield className="w-3 h-3 mr-1" />
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.rep_tier ? (
                          <div>
                            <Badge className={getTierBadge(user.rep_tier)}>
                              <DollarSign className="w-3 h-3 mr-1" />
                              {user.rep_tier}
                            </Badge>
                            {user.rep_tier === 'Director' && user.redline_ppw != null && (
                              <span className="ml-2 text-xs text-amber-600 font-medium">
                                RL: ${user.redline_ppw}/W
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {user.manager_name && (
                            <p className="flex items-center gap-1">
                              <Crown className="w-3 h-3 text-amber-500" />
                              <span className="text-slate-600 dark:text-slate-400">{user.manager_name}</span>
                            </p>
                          )}
                          {user.director_name && (
                            <p className="flex items-center gap-1">
                              <Crown className="w-3 h-3 text-emerald-500" />
                              <span className="text-slate-600 dark:text-slate-400">{user.director_name}</span>
                            </p>
                          )}
                          {!user.manager_name && !user.director_name && (
                            <span className="text-slate-400">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {user.id !== currentUser?.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`user-actions-${user.id}`}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => openRoleDialog(user)}
                                data-testid={`change-role-menu-${user.id}`}
                              >
                                <Shield className="w-4 h-4 mr-2" />
                                Change Role
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => openTierDialog(user)}
                                data-testid={`set-tier-menu-${user.id}`}
                              >
                                <DollarSign className="w-4 h-4 mr-2" />
                                Set Commission Tier
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openHierarchyDialog(user)}
                                data-testid={`assign-hierarchy-menu-${user.id}`}
                              >
                                <Crown className="w-4 h-4 mr-2" />
                                Assign Manager/Director
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedUser(user);
                                  setNewPassword('');
                                  setResetPasswordDialogOpen(true);
                                }}
                                data-testid={`reset-password-menu-${user.id}`}
                              >
                                <Key className="w-4 h-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              {user.id !== currentUser?.id && (
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-red-600 focus:text-red-600"
                                  data-testid={`delete-user-menu-${user.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete User
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <UsersIcon className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No active users
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Users will appear here once they have been approved.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Change Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update the role for {selectedUser?.name}. This will change their access permissions.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger data-testid="role-select">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Admin">Admin - Full access</SelectItem>
                <SelectItem value="SalesRep">Sales Rep - Lead management</SelectItem>
                <SelectItem value="BackOffice">Back Office - Pipeline & payouts</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRoleChange}
              disabled={updatingId === selectedUser?.id}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-role-btn"
            >
              {updatingId === selectedUser?.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Tier Dialog */}
      <Dialog open={tierDialogOpen} onOpenChange={setTierDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Set Commission Tier
            </DialogTitle>
            <DialogDescription>
              Set the commission tier for {selectedUser?.name}. This determines their commission rate and override eligibility.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Commission Tier</Label>
              <Select value={newTier} onValueChange={setNewTier}>
                <SelectTrigger data-testid="tier-select">
                  <SelectValue placeholder="Select commission tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Tier Assigned</SelectItem>
                  {REP_TIERS.map((tier) => (
                    <SelectItem key={tier} value={tier}>
                      {tier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newTier && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-sm">
                <p className="font-medium mb-1">Commission Rates:</p>
                <ul className="text-slate-600 dark:text-slate-400 space-y-1">
                  {newTier === 'Broker 1' && <li>• $100/kW for self-sold deals</li>}
                  {newTier === 'Broker 2' && <li>• $200/kW for self-sold deals</li>}
                  {newTier === 'Manager' && (
                    <>
                      <li>• $350/kW for self-sold deals</li>
                      <li>• $250 override on Broker 1 deals</li>
                      <li>• $150 override on Broker 2 deals</li>
                    </>
                  )}
                  {newTier === 'Director' && (
                    <>
                      <li>• $350/kW for self-sold deals</li>
                      <li>• $50 override on Manager deals</li>
                      <li>• $250 override on Broker 1 (no manager)</li>
                      <li className="text-amber-600 font-medium">• Red Line: Custom PPW rate (see below)</li>
                    </>
                  )}
                  {newTier === 'Retail Rep' && <li>• $50/kW for DC market deals</li>}
                </ul>
              </div>
            )}
            
            {/* Red Line PPW Input - Only for Directors */}
            {newTier === 'Director' && (
              <div className="space-y-2 mt-4 p-4 border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20 rounded-lg">
                <Label className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <DollarSign className="w-4 h-4" />
                  Red Line PPW Rate ($/W)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g., 2.50 (leave empty to disable)"
                  value={redlinePpw}
                  onChange={(e) => setRedlinePpw(e.target.value)}
                  className="bg-white dark:bg-slate-800"
                  data-testid="redline-ppw-input"
                />
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  <strong>Red Line Formula:</strong> (Deal PPW - Red Line PPW) × System Size × 1000 = Director Total Commission
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Team member commissions are automatically deducted from the Director total.
                  Leave empty to use standard Director override rates instead.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTierDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleTierChange}
              disabled={updatingId === selectedUser?.id}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-tier-btn"
            >
              {updatingId === selectedUser?.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Save Tier'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hierarchy Dialog */}
      <Dialog open={hierarchyDialogOpen} onOpenChange={setHierarchyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Assign Team Hierarchy
            </DialogTitle>
            <DialogDescription>
              Assign {selectedUser?.name} to a manager and/or director. This determines override payouts.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-500" />
                Manager
              </Label>
              <Select value={newManagerId} onValueChange={setNewManagerId}>
                <SelectTrigger data-testid="manager-select">
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {managers.filter(m => m.id !== selectedUser?.id).map((mgr) => (
                    <SelectItem key={mgr.id} value={mgr.id}>
                      {mgr.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-emerald-500" />
                Director
              </Label>
              <Select value={newDirectorId} onValueChange={setNewDirectorId}>
                <SelectTrigger data-testid="director-select">
                  <SelectValue placeholder="Select director" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Director</SelectItem>
                  {directors.filter(d => d.id !== selectedUser?.id).map((dir) => (
                    <SelectItem key={dir.id} value={dir.id}>
                      {dir.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHierarchyDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleHierarchyChange}
              disabled={updatingId === selectedUser?.id}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-hierarchy-btn"
            >
              {updatingId === selectedUser?.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Save Hierarchy'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-500" />
              Add Team Member
            </DialogTitle>
            <DialogDescription>
              Create a new user account. They will be able to login immediately with these credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-user-name">Full Name *</Label>
              <Input
                id="new-user-name"
                placeholder="John Smith"
                value={newUserData.name}
                onChange={(e) => setNewUserData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="new-user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email Address *</Label>
              <Input
                id="new-user-email"
                type="email"
                placeholder="john@company.com"
                value={newUserData.email}
                onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                data-testid="new-user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-password">Password *</Label>
              <Input
                id="new-user-password"
                type="password"
                placeholder="Min 8 characters"
                value={newUserData.password}
                onChange={(e) => setNewUserData(prev => ({ ...prev, password: e.target.value }))}
                data-testid="new-user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select 
                value={newUserData.role} 
                onValueChange={(value) => setNewUserData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger data-testid="new-user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role === 'SalesRep' ? 'Sales Rep' : role === 'BackOffice' ? 'Back Office' : role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newUserData.role === 'SalesRep' && (
              <div className="space-y-2">
                <Label>Commission Tier (optional)</Label>
                <Select 
                  value={newUserData.rep_tier || 'none'} 
                  onValueChange={(value) => setNewUserData(prev => ({ ...prev, rep_tier: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger data-testid="new-user-tier-select">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Tier</SelectItem>
                    {REP_TIERS.map((tier) => (
                      <SelectItem key={tier} value={tier}>
                        {tier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddUser}
              disabled={addingUser}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="create-user-btn"
            >
              {addingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Import Users Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-500" />
              Import Team Members
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import users. Download the template to see the required format.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-emerald-500" />
                <div>
                  <p className="font-medium">CSV Template</p>
                  <p className="text-sm text-slate-500">Download the import template</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label>Upload CSV File</Label>
              <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="hidden"
                  id="csv-upload"
                  data-testid="csv-file-input"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {importFile ? importFile.name : 'Click to select CSV file'}
                  </p>
                </label>
              </div>
            </div>

            <div className="text-sm text-slate-500 space-y-1">
              <p><strong>Required columns:</strong> name, email, password</p>
              <p><strong>Optional columns:</strong> role (Admin/SalesRep/BackOffice), rep_tier</p>
              <p><strong>Valid tiers:</strong> Broker 1, Broker 2, Manager, Director, Retail Rep</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportDialogOpen(false); setImportFile(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleImportUsers}
              disabled={importing || !importFile}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="import-submit-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Users
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name}. They will be notified via email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                data-testid="admin-reset-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setNewPassword('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resettingPassword || !newPassword || newPassword.length < 8}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-reset-password-btn"
            >
              {resettingPassword ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUser?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">
                <strong>Warning:</strong> Deleting this user will remove all their access to the system. 
                Any leads assigned to them will need to be reassigned.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteUser}
              disabled={deletingUser}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user-btn"
            >
              {deletingUser ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
